import Locale from '@formatjs/intl-locale';
import type { Category, LanguageAlternate } from '@hub/models/data';
import { Logger } from '@icommerce/logger';
import getNextConfig from 'next/config';
import type { NextRuntimeConfiguration } from '@hub/libs/config';
import {
  getDefaultLocaleForDomain,
  locales,
  translations as resources,
  defaultLocale,
  getDomainsForEnv,
  supportedLocales,
  generateFallbackLng,
} from '@icommerce/i18n';
import type { ContinentItem, LocaleItem, Translations } from '@icommerce/i18n';
// eslint-disable-next-line import/no-cycle
import {
  MAIN_IBH_CATEGORY_KEY,
  MAIN_IGLIDE_IBH_CATEGORY_KEY,
  type MetricType,
  RELEASED_ORDER_TRACKING_LOCALES,
} from './constants';
import { SHAPE_DESCRIPTORS, SUPPORTED_IGLIDE_IBH_LOCALES } from './bearing-hub.helper';
import type { ShapeDescriptor } from '@hub/bearing-hub/common/types';
import { getCategoryName } from '@hub/bearing-hub/common/link.factory';
import type { IbhUnitSystem } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import type { UserConfig, I18n } from 'next-i18next';
import type { Resource } from 'i18next';
import type { serverSideTranslations } from 'next-i18next/serverSideTranslations';

interface SupportedDomainItem {
  locale: string;
  domain: string;
  path: string;
}

const { publicRuntimeConfig } = getNextConfig() as NextRuntimeConfiguration;

const fallbackLng = generateFallbackLng(supportedLocales);

const domains = getDomainsForEnv(publicRuntimeConfig.envName);

export const LOCALIZED_ORDER_TRACKING_PATHS = {
  'x-default': '/info/company-order-tracking',
  'de-DE': '/info/auftragsverfolgung',
};

const SERVERSIDE_NEXT_I18N_CONFIG: UserConfig = {
  localePath: null,
  fallbackNS: 'common',
  fallbackLng,
  i18n: {
    localeDetection: false,
    defaultLocale,
    locales: supportedLocales,
    domains,
  },
};

const getOptimizdResources = (locale: string, namespaces: string[]): Resource => {
  const filterObjectByKeys = <T extends object>(obj: T, keysToKeep: string[]): Partial<T> =>
    Object.keys(obj)
      .filter(key => keysToKeep.includes(key))
      .reduce((newObj, key) => {
        // eslint-disable-next-line no-param-reassign
        newObj[key as keyof T] = obj[key as keyof T];
        return newObj;
      }, {} as Partial<T>);

  const optimizedRessources: Translations = {
    [locale]: filterObjectByKeys(resources[locale] as Translations, namespaces),
  };

  const fallbacks: string[] = fallbackLng[locale] as string[];
  if (fallbacks) {
    fallbacks.forEach(fallbackLocale => {
      if (resources[fallbackLocale]) {
        optimizedRessources[fallbackLocale] = filterObjectByKeys(resources[fallbackLocale] as Translations, namespaces);
      }
    });
  }

  return optimizedRessources as Resource;
};

export const getServerSideNextI18nConfig = (locale: string, namespaces: string[]): UserConfig => ({
  ...SERVERSIDE_NEXT_I18N_CONFIG,
  resources: getOptimizdResources(locale, namespaces),
});

export const clientSideNextI18nConfig = {
  ...SERVERSIDE_NEXT_I18N_CONFIG,
  onPreInitI18next: (i18nObj: I18n) => {
    const i18NOptions = i18nObj.options;
    if (i18NOptions) {
      const locale = i18NOptions.lng;
      const namespaces = i18NOptions.ns;

      i18NOptions.resources = getOptimizdResources(locale, namespaces as string[]);
    }
  },
};

export const getServerSideTranslations = async (
  serverSideTranslationsHandlerFunc: typeof serverSideTranslations,
  locale: string,
  namespaces: string[]
) => {
  const ssrConfig = await serverSideTranslationsHandlerFunc(
    locale,
    namespaces,
    getServerSideNextI18nConfig(locale, namespaces)
  );

  // Remove initialI18nStore for date reduction - will be build-up client side using resources
  // eslint-disable-next-line no-underscore-dangle
  delete ssrConfig?._nextI18Next?.initialI18nStore;

  return ssrConfig;
};

export const supportedDomains: SupportedDomainItem[] = publicRuntimeConfig.i18n.domains.reduce(
  (list: SupportedDomainItem[], domainConfig) => {
    list.push({
      locale: domainConfig.defaultLocale,
      domain: domainConfig.domain,
      path: '',
    });

    domainConfig.locales.forEach(locale => {
      if (publicRuntimeConfig.i18n.locales.includes(locale)) {
        list.push({
          locale,
          domain: domainConfig.domain,
          path: `/${locale}`,
        });
      } else {
        Logger.warn(
          {
            message: 'Got unsupported locale in domainConfig.',
            domain: domainConfig.domain,
            locale,
            supportedLocales: publicRuntimeConfig.i18n.locales,
          },
          'UnsupportedDomain'
        );
      }
    });
    return list;
  },
  []
);

export const supportedDomainsByLocale = Object.fromEntries(supportedDomains.map(item => [item.locale, item]));

export const getLocaleItemForLocaleString = (localeString: string): LocaleItem =>
  (locales as ContinentItem[])
    .flatMap(continent => continent.countries)
    .flatMap(country => country.languages)
    .find(locale => locale.code === localeString);

export const getLocaleWithFallbackRegion = (locale: string): string => {
  // special case: do not attach a "-DE" to default locale
  if (locale === 'default') {
    return locale;
  }
  const localeObject = new Locale(locale);
  if (!localeObject.region) {
    return `${locale}-DE`;
  }
  return locale;
};

// we need to check if the current locale is a configured defaultLocale. As every locale is unique, we can use the locale as key
export const isDefaultLocale = (locale?: string): boolean => {
  if (!locale) {
    return false;
  }
  const domainConfigs = publicRuntimeConfig.i18n.domains as {
    domain: string;
    defaultLocale: string;
    locales: string[];
  }[];
  return domainConfigs.map(domain => domain.defaultLocale).includes(locale);
};

/**
 * Takes a defaultAlternate and attaches the needed path to it.
 * @returns languageAlternate with the path attached
 * @param asPath
 * @param locale
 */

export const getCanonical = (asPath: string, locale?: string): string => {
  const path = asPath?.split(/[?#]/)?.[0];
  if (!isDefaultLocale(locale)) {
    return `${locale ? `/${locale}` : ''}${path}`;
  }
  return path;
};

// generates a list of domains in the format specified by languageAlternates for Next SEO
export const getLanguageAlternates = (): LanguageAlternate[] => [
  { hrefLang: 'x-default', href: 'https://www.igus.eu' },
  ...locales.flatMap((continent: ContinentItem) =>
    continent.countries.flatMap(country =>
      country.languages.flatMap(locale => ({
        hrefLang: locale.code,
        // add localeCode (e.g. '/fr-CH') when not using the default language for the domain
        href: `https://${country.baseUrl}${
          locale.code !== getDefaultLocaleForDomain(country.baseUrl) ? `/${locale.code}` : ''
        }`,
      }))
    )
  ),
];

export const getProductLanguageAlternates = (
  productCmsId: number | null | undefined,
  productKey: string
): LanguageAlternate[] =>
  getLanguageAlternates()
    .map(alternate => {
      const { hrefLang, href } = alternate;
      const cmsProductIdBasedHref = `${href}/product/${productCmsId || ''}${alternate.L ? `?L=${alternate.L}` : ''}`;
      const productKeyBasedHref = `${href}/product/${productKey}`;

      if (alternate.hrefLang === 'x-default') {
        return { hrefLang, href: productKeyBasedHref };
      }

      const supportedDomain = supportedDomainsByLocale[hrefLang];
      if (!productCmsId && !supportedDomain) {
        return null;
      }

      if (supportedDomain) {
        return { hrefLang, href: `https://${supportedDomain.domain}${supportedDomain.path}/product/${productKey}` };
      }

      return { hrefLang, href: cmsProductIdBasedHref };
    })
    .filter(Boolean);

export const getCategoryLanguageAlternate = (
  category: Category,
  defaultAlternate: LanguageAlternate,
  localizedPaths: Record<string, string>
): LanguageAlternate => {
  const fallbackPath = localizedPaths?.default || category.path;
  const alternatePath = `${localizedPaths?.[defaultAlternate.hrefLang] ?? fallbackPath}${
    defaultAlternate.L ? `?L=${defaultAlternate.L}` : ''
  }`;

  const supportedDomain = supportedDomainsByLocale[defaultAlternate.hrefLang];
  const alternatePrefix = supportedDomain
    ? `https://${supportedDomain.domain}${supportedDomain.path}`
    : defaultAlternate.href;

  return {
    hrefLang: defaultAlternate.hrefLang,
    href: `${alternatePrefix}${alternatePath}`,
  };
};

export const getCategoryLanguageAlternates = (category: Category, currentLocale: string): LanguageAlternate[] => {
  const categoryAlternatesLocale: string[][] = Object.entries(category.alternates ?? {}).map(([locale, path]) => [
    getLocaleWithFallbackRegion(locale),
    path,
  ]);

  const alternatesConverted = Object.fromEntries([
    [currentLocale, category.path],
    ...categoryAlternatesLocale,
  ]) as Record<string, string>;

  return getLanguageAlternates().map(alternate => {
    // check if the current alternates locale is handled by hub-frontend
    const isSupportedDomain = alternate.hrefLang in supportedDomainsByLocale;
    // use the converted Alternates as localized paths if it is a supported domain and otherwise the aliases

    return getCategoryLanguageAlternate(
      category,
      alternate,
      isSupportedDomain || !category.aliases ? alternatesConverted : category.aliases
    );
  });
};

export const getOrderTrackingLanguageAlternates = (): LanguageAlternate[] =>
  getLanguageAlternates()
    .filter(({ hrefLang }) => RELEASED_ORDER_TRACKING_LOCALES.includes(hrefLang))
    .map(({ hrefLang, href }) => ({
      hrefLang,
      href: `${href}${
        LOCALIZED_ORDER_TRACKING_PATHS[hrefLang]
          ? String(LOCALIZED_ORDER_TRACKING_PATHS[hrefLang])
          : LOCALIZED_ORDER_TRACKING_PATHS['x-default']
      }`,
    }));

export const getLanguageAlternatesSupportedByIbh = () =>
  getLanguageAlternates().filter(langAlternate => {
    const languageIsSupportedByIbh = Object.keys(SHAPE_DESCRIPTORS).includes(langAlternate.hrefLang);
    const isDefault = langAlternate.hrefLang === 'x-default';

    return languageIsSupportedByIbh || isDefault;
  });

export const getIbhLanguageAlternates = (
  locale: string,
  {
    selectedShape,
    productKey,
    materialName,
    unitSystem,
  }: {
    selectedShape?: ShapeDescriptor;
    productKey?: string;
    materialName?: string;
    unitSystem?: IbhUnitSystem;
  }
): LanguageAlternate[] =>
  getLanguageAlternatesSupportedByIbh().map(alternate => {
    const supportedDomain = supportedDomainsByLocale[alternate.hrefLang];
    const categoryName = materialName && getCategoryName(materialName, unitSystem as unknown as MetricType);
    const shapeDescriptorPath =
      SHAPE_DESCRIPTORS[supportedDomain ? alternate.hrefLang : 'en'][selectedShape?.shapeKey]?.path;
    const resultPagePath = `${alternate.href}/${
      SUPPORTED_IGLIDE_IBH_LOCALES.includes(alternate.hrefLang) ? MAIN_IGLIDE_IBH_CATEGORY_KEY : MAIN_IBH_CATEGORY_KEY
    }/${shapeDescriptorPath}`;
    const productDetailPagePath = `${resultPagePath}/product-details/${categoryName}`;

    return {
      hrefLang: alternate.hrefLang,
      href: productKey ? productDetailPagePath : resultPagePath,
    };
  });

export const getFfhLanguageAlternates = (query: Record<string, string>, productKey?: string): LanguageAlternate[] => {
  const queryString = new URLSearchParams(query).toString();
  return getLanguageAlternates().map(alternate => {
    const basePath = `${alternate.href}/free-form-hub`;
    const path = productKey ? `${basePath}/${productKey}?${queryString}` : `${basePath}?${queryString}`;

    return {
      hrefLang: alternate.hrefLang,
      href: path,
    };
  });
};
